import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { TTopRestaurant } from '../schemas';

type TopRestaurantsTableProps = {
  topRestaurants: TTopRestaurant[];
  isLoading?: boolean;
  onRestaurantClick?: (restaurantId: string, restaurantName: string) => void;
};

const TopRestaurantsTable = ({ topRestaurants, isLoading, onRestaurantClick }: TopRestaurantsTableProps) => {
  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.title}>Top Restaurants by Customer Count</Typography>
        <Typography sx={styles.loadingText}>Loading top restaurants...</Typography>
      </Box>
    );
  }

  if (!topRestaurants || topRestaurants.length === 0) {
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.title}>Top Restaurants by Customer Count</Typography>
        <Typography sx={styles.emptyText}>No restaurant data available</Typography>
      </Box>
    );
  }

  const getRankIcon = (index: number) => {
    const colors = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
    return index < 3 ? (
      <EmojiEventsIcon sx={{ color: colors[index], fontSize: 24 }} />
    ) : (
      <Typography sx={styles.rankNumber}>{index + 1}</Typography>
    );
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>Top Restaurants by Customer Count</Typography>

      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCell} width="80px">Rank</TableCell>
              <TableCell sx={styles.headerCell}>Restaurant Name</TableCell>
              <TableCell sx={styles.headerCell} align="right">Customer Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topRestaurants.map((restaurant, index) => (
              <TableRow
                key={restaurant.restaurantId}
                onClick={() => onRestaurantClick?.(restaurant.restaurantId, restaurant.restaurantName)}
                sx={{
                  ...styles.tableRow,
                  backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                  cursor: onRestaurantClick ? 'pointer' : 'default',
                }}
              >
                <TableCell sx={styles.rankCell}>
                  <Box sx={styles.rankContainer}>
                    {getRankIcon(index)}
                  </Box>
                </TableCell>
                <TableCell sx={styles.nameCell}>
                  {restaurant.restaurantName}
                </TableCell>
                <TableCell sx={styles.countCell} align="right">
                  <Box sx={styles.countBadge}>
                    {restaurant.customerCount.toLocaleString()}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TopRestaurantsTable;

const styles = {
  container: {
    border: '1.5px solid #AAA',
    borderRadius: 2,
    backgroundColor: '#FFF',
    padding: 3,
  },
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
    marginBottom: 2,
  },
  loadingText: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    padding: 4,
  },
  emptyText: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    padding: 4,
  },
  tableContainer: {
    borderRadius: 2,
    border: '1px solid #eee',
  },
  headerCell: {
    fontFamily: 'GilroyBold',
    fontSize: 14,
    backgroundColor: '#f5f5f5',
    padding: '12px 16px',
    borderBottom: '2px solid #ddd',
  },
  tableRow: {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0 !important',
      transform: 'scale(1.01)',
    },
  },
  rankCell: {
    padding: '12px 16px',
  },
  rankContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rankNumber: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
    color: '#666',
  },
  nameCell: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    padding: '12px 16px',
  },
  countCell: {
    padding: '12px 16px',
  },
  countBadge: {
    fontFamily: 'GilroyBold',
    fontSize: 16,
    backgroundColor: '#000',
    color: '#fff',
    padding: '6px 16px',
    borderRadius: '6px',
    display: 'inline-block',
  },
};
